import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "./AuthContext";

function centsToDollars(cents) {
  if (cents == null || isNaN(cents)) return "0.00";
  const abs = Math.abs(parseInt(cents, 10));
  return `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

export default function OrdersPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await adminApi.get("/admin/orders", {
        params: filter ? { fulfillment_status: filter } : {},
      });
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const oversoldCount = items.filter(
    (o) => o.fulfillment_status === "failed_oversold"
  ).length;

  return (
    <section data-testid="admin-orders-panel" style={{ marginTop: 36 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          margin: "0 0 16px 0",
        }}
      >
        <h2 style={{ fontSize: 16, letterSpacing: 1, margin: 0 }}>ORDERS</h2>
        <select
          data-testid="orders-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            padding: "6px 8px",
            background: "#0A0F1F",
            color: "#fff",
            border: "1px solid #2a3556",
            fontSize: 12,
          }}
        >
          <option value="">All fulfillment states</option>
          <option value="pending">pending</option>
          <option value="fulfilled">fulfilled</option>
          <option value="failed_oversold">failed_oversold</option>
        </select>
        {oversoldCount > 0 && filter !== "failed_oversold" && (
          <span
            data-testid="orders-oversold-warning"
            style={{
              color: "#0A0F1F",
              background: "#ff6b6b",
              padding: "2px 8px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            ⚠ {oversoldCount} OVERSOLD — REFUND REQUIRED
          </span>
        )}
      </div>

      {err && (
        <div style={{ color: "#ff6b6b", margin: "10px 0" }} data-testid="orders-error">
          {err}
        </div>
      )}

      {loading ? (
        <div>Loading orders…</div>
      ) : items.length === 0 ? (
        <div style={{ opacity: 0.6 }}>No orders yet.</div>
      ) : (
        <table data-testid="orders-table" style={tableStyle}>
          <thead>
            <tr style={trHead}>
              <th style={th}>BUYER</th>
              <th style={th}>EVENT</th>
              <th style={th}>TIER</th>
              <th style={th}>QTY</th>
              <th style={th}>TOTAL</th>
              <th style={th}>PAYMENT</th>
              <th style={th}>FULFILLMENT</th>
              <th style={th}>EMAIL</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <OrderRow key={o.id} o={o} onChanged={load} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function OrderRow({ o, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const resend = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await adminApi.post(
        `/admin/orders/${o.id}/resend-confirmation`
      );
      setMsg(`Resent: ${data.send_result}`);
      onChanged();
    } catch (e) {
      setMsg(formatApiError(e.response?.data?.detail) || "Resend failed");
    } finally {
      setBusy(false);
    }
  };

  const sentAt = o.confirmation_email_sent_at
    ? new Date(o.confirmation_email_sent_at).toLocaleString("en-US", {
        timeZone: "America/Denver",
      })
    : null;

  return (
    <tr
      data-testid={`order-row-${o.id}`}
      style={
        o.fulfillment_status === "failed_oversold"
          ? { ...trBody, background: "rgba(255,107,107,0.08)" }
          : trBody
      }
    >
      <td style={td}>
        <div>{o.buyer_name || "—"}</div>
        <div style={{ fontSize: 11, opacity: 0.6 }}>{o.buyer_email}</div>
      </td>
      <td style={td}>{o.event_name}</td>
      <td style={td}>{o.tier_name}</td>
      <td style={td}>{o.quantity}</td>
      <td style={td}>${centsToDollars(o.total_cents)}</td>
      <td style={td}>{o.status}</td>
      <td style={td} data-testid={`order-fulfillment-${o.id}`}>
        <Pill status={o.fulfillment_status} />
      </td>
      <td style={td} data-testid={`order-email-${o.id}`}>
        <EmailPill status={o.confirmation_email_status} />
        {sentAt && (
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>{sentAt} MT</div>
        )}
      </td>
      <td style={{ ...td, textAlign: "right" }}>
        {o.fulfillment_status === "fulfilled" && (
          <button
            data-testid={`order-resend-${o.id}`}
            onClick={resend}
            disabled={busy}
            style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}
          >
            {busy ? "Sending…" : "Resend email"}
          </button>
        )}
        {msg && (
          <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>{msg}</div>
        )}
      </td>
    </tr>
  );
}

function EmailPill({ status }) {
  const colors = {
    sent: "#5dd6a4",
    failed: "#ff6b6b",
    not_sent: "#9aa8c4",
  };
  return (
    <span
      style={{
        padding: "2px 8px",
        background: colors[status] || "#9aa8c4",
        color: "#0A0F1F",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
      }}
    >
      {String(status || "not_sent").toUpperCase()}
    </span>
  );
}

function Pill({ status }) {
  const colors = {
    pending: "#9aa8c4",
    fulfilled: "#5dd6a4",
    failed_oversold: "#ff6b6b",
  };
  return (
    <span
      style={{
        padding: "2px 8px",
        background: colors[status] || "#9aa8c4",
        color: "#0A0F1F",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
      }}
    >
      {String(status || "").toUpperCase()}
    </span>
  );
}

const btnSecondary = {
  padding: "8px 14px",
  background: "transparent",
  color: "#fff",
  border: "1px solid #2a3556",
  cursor: "pointer",
};
const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 16 };
const th = { padding: "10px 8px", fontWeight: 600 };
const td = { padding: "10px 8px", fontSize: 14, verticalAlign: "top" };
const trHead = { textAlign: "left", fontSize: 11, opacity: 0.6 };
const trBody = { borderTop: "1px solid #14213D" };
